import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { RefreshCw, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AuctionLogsPage = () => {
  const { apiCall } = useAuth();
  const [auctionLogs, setAuctionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSlot, setFilterSlot] = useState('');

  useEffect(() => {
    fetchAuctionLogs();
  }, []);

  const fetchAuctionLogs = async () => {
    try {
      setLoading(true);
      // Mock data for now - will implement backend endpoint later
      const mockLogs = [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          slot_id: 'SLOT_HOME_VIDEO_CAROUSEL_01',
          winning_campaign_id: 'camp_123',
          vendor_id: 'vendor_456',
          vendor_name: 'TechStore India',
          winning_bid: 250.00,
          second_bid: 200.00,
          quality_score: 0.85,
          final_price: 201.00,
          impressions_won: 1
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          slot_id: 'SLOT_SEARCH_TOP_SPONSORED',
          winning_campaign_id: 'camp_789',
          vendor_id: 'vendor_321',
          vendor_name: 'Fashion Hub',
          winning_bid: 15.50,
          second_bid: 12.00,
          quality_score: 0.92,
          final_price: 12.10,
          impressions_won: 1
        }
      ];
      setAuctionLogs(mockLogs);
    } catch (error) {
      toast.error('Failed to load auction logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = filterSlot
    ? auctionLogs.filter(log => log.slot_id.includes(filterSlot.toUpperCase()))
    : auctionLogs;

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Ad Auction Logs</h1>
            <p className="text-gray-600 mt-2">Real-time monitoring of ad auction results</p>
          </div>
          <Button onClick={fetchAuctionLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Total Auctions (Last 24h)</p>
                <p className="text-2xl font-bold">{auctionLogs.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Avg Winning Bid</p>
                <p className="text-2xl font-bold">
                  ₹{auctionLogs.length > 0 
                    ? (auctionLogs.reduce((sum, l) => sum + l.winning_bid, 0) / auctionLogs.length).toFixed(2)
                    : '0.00'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Avg Quality Score</p>
                <p className="text-2xl font-bold">
                  {auctionLogs.length > 0 
                    ? (auctionLogs.reduce((sum, l) => sum + l.quality_score, 0) / auctionLogs.length).toFixed(2)
                    : '0.00'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Unique Vendors</p>
                <p className="text-2xl font-bold">
                  {new Set(auctionLogs.map(l => l.vendor_id)).size}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-gray-500" />
              <Input
                placeholder="Filter by Slot ID (e.g., SLOT_HOME_VIDEO)"
                value={filterSlot}
                onChange={(e) => setFilterSlot(e.target.value)}
                className="max-w-md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Auction Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Auction Results</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-12 text-gray-500">Loading auction logs...</p>
            ) : filteredLogs.length === 0 ? (
              <p className="text-center py-12 text-gray-500">No auction logs found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slot ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Winning Vendor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Winning Bid</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">2nd Bid</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quality Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Final Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{formatTimestamp(log.timestamp)}</td>
                        <td className="px-4 py-3 text-sm font-mono">
                          <Badge variant="outline">{log.slot_id}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{log.vendor_name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                          {log.winning_campaign_id.slice(0, 10)}...
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                          ₹{log.winning_bid.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          ₹{log.second_bid.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge className={
                            log.quality_score >= 0.8 ? 'bg-green-100 text-green-800' :
                            log.quality_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {log.quality_score.toFixed(2)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">
                          ₹{log.final_price.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>How Second-Price Auction Works:</strong> The winner pays the second-highest bid plus ₹0.01. 
                    Final Price = (Second Bid * Quality Score) + ₹0.01. This encourages truthful bidding and rewards quality.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AuctionLogsPage;
